import {ILimitedResults, IResultsData} from '../Types/IResultsData';
import {
    IGroupedSearchFilterItem,
    ISearchFilter,
    ISearchFilterItem,
    ISearchFilterSet,
    ISelectedFilters
} from '../Types/ISearchFilter';
import {
    IGenericNumberObject,
    IGenericObject,
    IGenericStringArrayObject
} from '../Types/IGenericObject';
import {DEFAULT_SEARCH_RADIUS, SEARCH_RADIUS} from '../Constants/SearchRadius';
import SearchFilterName from '../Enum/SearchFilterName';
import {getSupplierName} from './SupplierUtils';
import {IBaseSearchParam} from '../Types/ISearchParam';
import VehicleGroupLabel from '../Enum/VehicleGroupLabel';
import VEHICLE_TYPES, {VEHICLE_GROUP_MAP} from '../Constants/VehicleTypes';
import IVehicleType from '../Types/IVehicleType';

type filterItemMapperConfig = {
    labelDecorator?: string,
    sortFn?: (a, b) => number,
    itemMapper?: (label: string, value: string | number) => ISearchFilterItem
}

const filterItemMapper = (
    item: IGenericObject | IGenericNumberObject,
    config?: filterItemMapperConfig
): ISearchFilterItem[] => {
    return Object.keys(item)
            .map((key) => {
                if (typeof config !== 'undefined' && typeof config.itemMapper !== 'undefined') {
                   return config.itemMapper(`${key}${typeof config.labelDecorator !== 'undefined' ? config.labelDecorator : ''}`, item[key]);
                }

                return {
                    label: `${key}${typeof config !== 'undefined' && typeof config.labelDecorator !== 'undefined' ? config.labelDecorator : ''}`,
                    value: item[key],
                }
            })
            .sort((a, b) => {
                if (typeof config !== 'undefined' &&  typeof config.sortFn !== 'undefined') {
                    return config.sortFn(a, b);
                }
                return a.label.localeCompare(b.label)
            });
}

export const generateFiltersFromResults = (data: IResultsData[], searchParameters: IBaseSearchParam): ISearchFilterSet => {
    const carTypeMap: IGenericObject = {};
    const vehicleNameMap: IGenericObject = {};
    const supplierCodeMap: IGenericObject = {};
    const durationMap: IGenericNumberObject = {};
    const carClassMap: IGenericObject = {};
    const depotMap: IGenericObject = {};
    const seatsMap: IGenericNumberObject = {};

    if (data.length !== 0) {
        let i: number = 0;
        let total: number = data.length;

        while (i < total) {
            supplierCodeMap[typeof data[i].supplier_name !== 'undefined' ? data[i].supplier_name : getSupplierName(data[i].supplier_code)] = data[i].supplier_code;
            carTypeMap[data[i].vehicle_data.type] = data[i].vehicle_data.type;
            vehicleNameMap[data[i].vehicle_data.name.replace('or similar', '')] = data[i].vehicle_data.name;
            carClassMap[data[i].vehicle_data.category] = data[i].vehicle_data.category;
            durationMap[data[i].duration] = data[i].duration;
            depotMap[`${data[i].depot_code} - ${data[i].supplier_name || getSupplierName(data[i].supplier_code)}`] = `${data[i].depot_code}_${data[i].supplier_code}`;
            seatsMap[data[i].vehicle_data.seats] = data[i].vehicle_data.seats;
            i++;
        }
    }

    const carTypeFilter: ISearchFilter = {
        name: SearchFilterName.CAR_TYPE,
        label: 'Vehicle type',
        disableMultiSelect: false,
        linkedFilter: SearchFilterName.CAR_MODEL,
        items: ((): ISearchFilterItem[] => {
            const groupedFilterItem: IGroupedSearchFilterItem = {
                [VehicleGroupLabel.SUV_WAGON]: {
                    label: VehicleGroupLabel.SUV_WAGON,
                    value: []
                },
                [VehicleGroupLabel.COUPE_CONVERTIBLE_SPORT]: {
                    label: VehicleGroupLabel.COUPE_CONVERTIBLE_SPORT,
                    value: []
                },
                [VehicleGroupLabel.PICKUP_UTE]: {
                    label: VehicleGroupLabel.PICKUP_UTE,
                    value: []
                },
                [VehicleGroupLabel.PASSENGERVAN_MONOSPACE]: {
                    label: VehicleGroupLabel.PASSENGERVAN_MONOSPACE,
                    value: []
                }
            }

            Object.keys(carTypeMap).forEach((carTypekey) => {
                if (
                    VEHICLE_GROUP_MAP.hasOwnProperty(carTypekey) &&
                    groupedFilterItem.hasOwnProperty(VEHICLE_GROUP_MAP[carTypekey])) {
                    groupedFilterItem[VEHICLE_GROUP_MAP[carTypekey]].value.push(carTypekey);
                } else {
                    groupedFilterItem[carTypekey] = {
                        label: carTypekey,
                        value: [carTypekey]
                    }
                }
            });

            return Object.keys(groupedFilterItem)
                .map((key: string) => {
                    const formattedLabel: string = groupedFilterItem[key].label.replace(/ /g, '');
                    const formattedVehicleType: string | undefined = typeof searchParameters.vehicleType !== 'undefined' ? searchParameters.vehicleType.replace(/,/g, '/') : undefined;

                    return {
                        label: groupedFilterItem[key].label,
                        value: groupedFilterItem[key].value.join(','),
                        default: typeof formattedVehicleType !== 'undefined' ? formattedLabel === formattedVehicleType : false,
                        disabled: typeof searchParameters.vehicleType !== 'undefined'
                    }
                })
                .filter((item) => item.value !== '');
        })()
    }

    const carClassFilter: ISearchFilter = {
        name: SearchFilterName.CAR_CLASS,
        label: 'Vehicle class',
        linkedFilter: SearchFilterName.CAR_MODEL,
        items: filterItemMapper(carClassMap)
    }

    const supplierFilter: ISearchFilter = {
        name: SearchFilterName.SUPPLIER,
        label: 'Supplier',
        items: filterItemMapper(supplierCodeMap)
    }

    const carModelFilter: ISearchFilter = {
            name: SearchFilterName.CAR_MODEL,
            label: 'Vehicle model',
            isSearchAble: true,
            isDynamic: true,
            items: filterItemMapper(
                vehicleNameMap,
                {
                    itemMapper: (label, value) => {
                        return {
                            label,
                            value,
                            hidden: false
                        }
                    }
                })
        }

    const radiusFilter: ISearchFilter = {
        name: SearchFilterName.RADIUS,
        label: 'Radius',
        disableMultiSelect: true,
        items: SEARCH_RADIUS.map((rad: number) => {
            return {
                label: `${rad}km`,
                value: rad,
            }
        })
    }

    const durationFilter: ISearchFilter = {
        name: SearchFilterName.DURATION,
        label: 'Duration',
        items: filterItemMapper(
            durationMap,
            {
                labelDecorator:  ' day(s)',
                sortFn: (a, b) => a.value > b.value ? 1 : -1
            }
        )
    }

    const depotFilter: ISearchFilter = {
        name: SearchFilterName.DEPOT,
        label: 'Depot',
        items: filterItemMapper(depotMap)
    }

    const seatFilter: ISearchFilter = {
        name: SearchFilterName.SEATS,
        label: 'Seats',
        items: filterItemMapper(
          seatsMap, {
            labelDecorator: ' seats',
            sortFn: (a, b) => a.value > b.value ? 1 : -1,
          }
        )
    }

    return {
        [SearchFilterName.DURATION]: durationFilter,
        [SearchFilterName.RADIUS]: radiusFilter,
        [SearchFilterName.CAR_TYPE]: carTypeFilter,
        [SearchFilterName.CAR_CLASS]: carClassFilter,
        [SearchFilterName.CAR_MODEL]: carModelFilter,
        [SearchFilterName.SUPPLIER]: supplierFilter,
        [SearchFilterName.DEPOT]: depotFilter,
        [SearchFilterName.SEATS]: seatFilter
    }
};

export const updateDynamicFilters = (newAvailableFilters: ISearchFilterSet, activeFilters: ISearchFilterSet): ISearchFilterSet => {
    const currentActiveFilters: ISearchFilterSet = {...activeFilters};

    Object.keys(currentActiveFilters).forEach((key) => {
        const linkedFilter: string | undefined = currentActiveFilters[key].linkedFilter;
        if (typeof linkedFilter === 'undefined') {
            return;
        }

        const linkedFiltersItems: ISearchFilterItem[] | undefined = currentActiveFilters[linkedFilter].items;
        const newAvailableItems: ISearchFilterItem[] | undefined = newAvailableFilters[linkedFilter].items;

        if (
            typeof linkedFilter !== 'undefined' &&
            typeof linkedFiltersItems !== 'undefined' &&
            typeof newAvailableItems !== 'undefined'
        ) {
            linkedFiltersItems.map((item) => {
                item.hidden = true;
                newAvailableItems.some((newItem: ISearchFilterItem) => {
                    if (newItem.value === item.value) {
                        item.hidden = false;
                    }

                    return newItem.value === item.value;
                });
                return item;
            });

            currentActiveFilters[linkedFilter].items = [...linkedFiltersItems];
            // prevent infinite loop
            currentActiveFilters[key].updateLinkedFilter = false;
        }
    });

    return currentActiveFilters;
}

export const resetFilterSet = (filters: ISearchFilterSet): ISearchFilterSet => {
    const newFilterSet: ISearchFilterSet = {...filters};
    Object.keys(newFilterSet).forEach((key: string) => {
        newFilterSet[key].hasSelection = false;
        const items = newFilterSet[key].items;
        if (typeof items !== 'undefined') {
            newFilterSet[key].items = items.map((item) => {
                item.selected = false;
                if (newFilterSet[key].isDynamic === true) {
                    item.hidden = false;
                }
                return item;
            });
        }
    });

    return newFilterSet;
}

export const getSelectedItemsValue = (filter: ISearchFilter | undefined): (string | number | boolean)[] | null => {
    if (typeof filter === 'undefined') {
        return null;
    }

    const items: ISearchFilterItem[] | undefined = filter.items;
    const result: (string | number | boolean)[]  = [];
    if (typeof items !== 'undefined') {
       items.forEach((item) => {
           if (item.selected === true) {
               result.push(item.value);
           }
       })
    }

    if (result.length === 0) {
        return null;
    }

    return result;
}

export const getSelectedRadiusFromActiveFilters = (activeFilters: ISearchFilterSet | null): number => {
    if (activeFilters === null || activeFilters.hasOwnProperty(SearchFilterName.RADIUS) === false) {
        return DEFAULT_SEARCH_RADIUS;
    }

    const selectedRadius: (string | number | boolean)[] | null = getSelectedItemsValue(activeFilters[SearchFilterName.RADIUS]);

    return selectedRadius !== null && typeof selectedRadius[0] === 'number' ? selectedRadius[0] : DEFAULT_SEARCH_RADIUS;
}

export const getFiltersSummary = (activeFilters: ISearchFilterSet | null, availableFilters: ISearchFilterSet | null): IGenericStringArrayObject | null => {
    if (availableFilters === null) {
        return null;
    }

    const filterSummary: IGenericStringArrayObject = {};

    Object.keys(availableFilters).forEach((key: string) => {
        const items: ISearchFilterItem[] | undefined = availableFilters[key].items;
        if (key === SearchFilterName.RADIUS ) {
            filterSummary[availableFilters[key].label] = [DEFAULT_SEARCH_RADIUS.toString()];
        } else if (typeof items !== 'undefined') {
            filterSummary[availableFilters[key].label] = items.map((item) => item.label);
        }
    });

    // check for active filters
    if (activeFilters !== null) {
        const carTypeFilters: (string | number | boolean)[] | null = getSelectedItemsValue(activeFilters[SearchFilterName.CAR_TYPE]);
        if (carTypeFilters !== null && filterSummary.hasOwnProperty(activeFilters[SearchFilterName.CAR_TYPE].label)) {
            filterSummary[activeFilters[SearchFilterName.CAR_TYPE].label] = carTypeFilters.map((item) => item.toString());
        }

        const carNameFilters: (string | number | boolean)[] | null = getSelectedItemsValue(activeFilters[SearchFilterName.CAR_MODEL]);
        if (carNameFilters !== null && filterSummary.hasOwnProperty(activeFilters[SearchFilterName.CAR_MODEL].label)) {
           filterSummary[activeFilters[SearchFilterName.CAR_MODEL].label] = carNameFilters.map((item) => item.toString());
        }

        const radiusFilter: (string | number | boolean)[] | null = getSelectedItemsValue(activeFilters[SearchFilterName.RADIUS]);
        if (radiusFilter !== null && filterSummary.hasOwnProperty(activeFilters[SearchFilterName.RADIUS].label)) {
            filterSummary[activeFilters[SearchFilterName.RADIUS].label] = radiusFilter.map((item) => item.toString());
        }

        const durationFilter: (string | number | boolean)[] | null = getSelectedItemsValue(activeFilters[SearchFilterName.DURATION]);
        if (durationFilter !== null && filterSummary.hasOwnProperty(activeFilters[SearchFilterName.DURATION].label)) {
            filterSummary[activeFilters[SearchFilterName.DURATION].label] = durationFilter.map((item) => item.toString());
        }
    }

    return filterSummary;
}

export const getActiveFiltersSelectedItems = (activeFilters: ISearchFilterSet | null): null | ISelectedFilters => {
    if (activeFilters === null) {
        return null;
    }

    const carTypeFilters: (string | number | boolean)[] | null = getSelectedItemsValue(activeFilters[SearchFilterName.CAR_TYPE]);
    const carNameFilters: (string | number | boolean)[] | null = getSelectedItemsValue(activeFilters[SearchFilterName.CAR_MODEL]);
    const carSupplierFilter: (string | number | boolean)[] | null = getSelectedItemsValue(activeFilters[SearchFilterName.SUPPLIER]);
    const radiusFilter: (string | number | boolean)[] | null = getSelectedItemsValue(activeFilters[SearchFilterName.RADIUS]);
    const durationFilter: (string | number | boolean)[] | null = getSelectedItemsValue(activeFilters[SearchFilterName.DURATION]);
    const carClassFilter: (string | number | boolean)[] | null = getSelectedItemsValue(activeFilters[SearchFilterName.CAR_CLASS]);
    const depotFilter: (string | number | boolean)[] | null = getSelectedItemsValue(activeFilters[SearchFilterName.DEPOT]);
    const seatsFilter: (string | number | boolean)[] | null = getSelectedItemsValue(activeFilters[SearchFilterName.SEATS]);

   return {
       [SearchFilterName.CAR_TYPE]: carTypeFilters,
       [SearchFilterName.CAR_MODEL]: carNameFilters,
       [SearchFilterName.SUPPLIER]: carSupplierFilter,
       [SearchFilterName.RADIUS]: radiusFilter,
       [SearchFilterName.DURATION]: durationFilter,
       [SearchFilterName.CAR_CLASS]: carClassFilter,
       [SearchFilterName.DEPOT]: depotFilter,
       [SearchFilterName.SEATS]: seatsFilter
   }
}

export const shouldFilterUpdate = (activeFilter: ISearchFilterSet): boolean => {
    let result: boolean = false;

    Object.keys(activeFilter).some((key) => {
       if (activeFilter[key].updateLinkedFilter === true) {
           result = true;
       }

       return activeFilter[key].updateLinkedFilter;
    });

    return result;
}

type vehicleTypesMapType = {
    byLabel: {
        [key: string]: IVehicleType
    },
    byType: {
        [key: string]: IVehicleType
    }
}
const getVehicleTypesMap = (): vehicleTypesMapType => {
    let result: vehicleTypesMapType = {
        byLabel: {},
        byType: {}
    };

    VEHICLE_TYPES.forEach((v: IVehicleType) => {
        if (!result.byLabel.hasOwnProperty(v.label)) {
            result.byLabel[v.label] = v;
        }
        if (!result.byType.hasOwnProperty(v.type)) {
            result.byType[v.type] = v;
        }
    });

    return result;
}

export const limitResultByVehicleType = (results: IResultsData[], vehicleType?: string): ILimitedResults => {
    const unrestrictedResult: ILimitedResults = {
        results,
        isLimited: false,
        limitedBy: null,
        availableVehicleTypes:[]
    }

    if (typeof vehicleType === 'undefined') {
        return unrestrictedResult
    }

    const vehicleTypesMap: vehicleTypesMapType = getVehicleTypesMap();
    const formattedVehicleType: string = vehicleType.replace(/,/g, '/');
    const availableVehTypes: IVehicleType[] = [];

    const filteredResult: IResultsData[] = results.filter((item) => {
        if (
            VEHICLE_GROUP_MAP.hasOwnProperty(item.vehicle_data.type) &&
            !availableVehTypes.hasOwnProperty(item.vehicle_data.type) &&
            vehicleTypesMap.byLabel.hasOwnProperty(item.vehicle_data.type) &&
            VEHICLE_GROUP_MAP[item.vehicle_data.type].replace(/ /g, '') !== formattedVehicleType
        ) {
            availableVehTypes[item.vehicle_data.type] = vehicleTypesMap.byLabel[item.vehicle_data.type];
        }

        return VEHICLE_GROUP_MAP.hasOwnProperty(item.vehicle_data.type) &&
            VEHICLE_GROUP_MAP[item.vehicle_data.type].replace(/ /g, '') === formattedVehicleType;
    });

    const vehicleTypeObj: IVehicleType | null = vehicleTypesMap.byType.hasOwnProperty(vehicleType)
        ? vehicleTypesMap.byType[vehicleType]
        :  null;

    const availableVehicleTypes: IVehicleType[] = Object.keys(availableVehTypes)
        .map((key: string) => availableVehTypes[key]);

    return {
        results: filteredResult,
        isLimited: true,
        limitedBy: vehicleTypeObj,
        availableVehicleTypes
    };
}
